/**
 * O campo "Grupo" da ficha nova da Empresa (13.18, chamado de 26/10/2026, E).
 *
 * "Na criação da empresa não tem como colocar … o grupo": a ficha nova escolhe um Grupo **que já
 * existe** (criar Grupo novo continua sendo na ficha, depois), e a Empresa nasce filial dele na
 * mesma transação. A lista vem de `GET /grupos` — só os Grupos que quem olha lê, cada um com as
 * filiais que a pessoa enxerga —, e a busca acha pelo nome do Grupo **e** pelo nome das filiais,
 * como o dossiê pede ("um Grupo existente ou a Empresa principal dele"), sem acento nem caixa.
 *
 * O campo só aparece com `editar_empresa` e `gerir_grupos_de_empresas` (quem monta decide):
 * conveniência, e não autorização — o serviço recusa com `403`, e o Grupo que a pessoa não lê é o
 * `404` do Grupo.
 *
 * A gramática é a do campo Empresa com sugestão (`campo-de-empresa.css`): um `combobox` com a lista
 * suspensa, setas, `Enter` e `Esc`.
 *
 * **O que se digitou e não se escolheu** não vira Grupo calado: o texto sobe a quem monta
 * (`aoTrocarOTexto`), que recusa o envio com `RECUSA_DO_TEXTO_SEM_GRUPO`. No `blur`, o texto igual
 * (sem diferença de caixa nem de pontas) ao nome de **um** Grupo só o escolhe. **O Grupo escolhido
 * que some da lista** (desfeito em outra tela, ou deixou de ser legível) sai da escolha, com aviso.
 *
 * **A lista abre em portal** (`document.body`, `position: fixed`), posicionada por
 * `usePosicaoDoPainel` — a mesma peça do painel das etiquetas (13.15): o cartão da ficha tem
 * `overflow: hidden` (os cantos arredondados), e a lista dentro dele sairia cortada na borda de
 * baixo. Ela tem a largura do campo, abre embaixo e sobe quando embaixo não cabe. Desde o code
 * review da 13.19c1, dentro de um diálogo o portal é o próprio diálogo (`conteinerDoPainel`), e a
 * camada é `--camada-painel-flutuante`, acima de todo véu de modal.
 */

import { type KeyboardEvent, type RefObject, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { type Grupo, useGrupos } from './api'
import { conteinerDoPainel, usePosicaoDoPainel } from './etiquetas'
import { nomeExibidoDaEmpresa, normalizarParaComparar } from './nome-da-empresa'
import './campo-de-empresa.css'

export const ROTULO_DO_CAMPO_DO_GRUPO = 'Grupo'
export const DICA_DO_CAMPO_DO_GRUPO = 'Busque pelo nome do Grupo ou de uma filial'
export const NENHUM_GRUPO_ENCONTRADO = 'Nenhum Grupo encontrado.'
export const SEM_GRUPOS = 'Nenhum Grupo que você possa ver.'
export const TIRAR_O_GRUPO = 'Tirar o Grupo'
export const GRUPO_INDISPONIVEL = 'O Grupo escolhido não está mais disponível. Escolha outro.'
export const RECUSA_DO_TEXTO_SEM_GRUPO = 'Escolha um Grupo da lista ou apague o texto.'

/** O único Grupo cujo nome é o texto (sem diferença de caixa nem de pontas), ou `undefined`. */
export function grupoDeNomeIgual(grupos: readonly Grupo[], texto: string): Grupo | undefined {
  const procurado = texto.trim().toLocaleLowerCase('pt-BR')
  if (procurado === '') {
    return undefined
  }
  const iguais = grupos.filter(
    (grupo) => grupo.nome.trim().toLocaleLowerCase('pt-BR') === procurado,
  )
  return iguais.length === 1 ? iguais[0] : undefined
}

/** As filiais de um Grupo, pelo nome exibido, como a opção as descreve. */
export function filiaisDoGrupo(grupo: Grupo): string {
  return grupo.filiais.map((filial) => nomeExibidoDaEmpresa(filial)).join(', ')
}

/**
 * Os Grupos que casam com o termo: pelo nome do Grupo ou pelo nome (exibido, ou a razão social) de
 * uma filial — sem acento, caixa nem pontuação. Termo vazio devolve todos.
 */
export function gruposQueCasam(grupos: readonly Grupo[], termo: string): Grupo[] {
  const procurado = normalizarParaComparar(termo)
  if (procurado === '') {
    return [...grupos]
  }
  return grupos.filter(
    (grupo) =>
      normalizarParaComparar(grupo.nome).includes(procurado) ||
      grupo.filiais.some(
        (filial) =>
          normalizarParaComparar(nomeExibidoDaEmpresa(filial)).includes(procurado) ||
          normalizarParaComparar(filial.razao_social).includes(procurado),
      ),
  )
}

export function CampoDoGrupo({
  grupoId,
  aoEscolher,
  aoTrocarOTexto,
  recusa = null,
  refDaEntrada,
  desabilitado = false,
}: {
  /** O Grupo escolhido, ou `null`. */
  grupoId: string | null
  aoEscolher: (grupoId: string | null) => void
  /** O que está digitado sem escolha — quem monta recusa o envio com ele. */
  aoTrocarOTexto?: (texto: string) => void
  /** A recusa do envio, no campo (o texto sem Grupo escolhido). */
  recusa?: string | null
  /** O campo, para quem monta levar o foco a ele junto com a recusa. */
  refDaEntrada?: RefObject<HTMLInputElement | null>
  desabilitado?: boolean
}) {
  const consulta = useGrupos(true)
  const id = useId()
  const idDaLista = `${id}-lista`
  const idDaDica = `${id}-dica`
  const [texto, setTexto] = useState('')
  const [aberta, setAberta] = useState(false)
  const [ativa, setAtiva] = useState(-1)
  const [sumiu, setSumiu] = useState(false)
  const idDaRecusa = `${id}-recusa`
  const entradaInterna = useRef<HTMLInputElement>(null)
  const entrada = refDaEntrada ?? entradaInterna
  const popup = useRef<HTMLDivElement>(null)
  /* Onde a lista mora: o diálogo do campo, quando há um (code review da 13.19c1). */
  const [conteiner, setConteiner] = useState<HTMLElement | null>(null)

  const grupos = consulta.data ?? []
  const escolhido = grupoId === null ? undefined : grupos.find((grupo) => grupo.id === grupoId)
  const opcoes = gruposQueCasam(grupos, texto)
  const listaAberta = aberta && !desabilitado && escolhido === undefined && consulta.isSuccess
  const posicao = usePosicaoDoPainel(listaAberta, entrada, popup, { daLarguraDaAncora: true })

  /* O escolhido que a lista relida não traz mais sai da escolha — e a tela diz por quê. */
  const fora =
    consulta.isSuccess && grupoId !== null && !consulta.data.some((g) => g.id === grupoId)
  if (fora && !sumiu) {
    setSumiu(true)
  }
  useEffect(() => {
    if (fora) {
      aoEscolher(null)
    }
  }, [fora, aoEscolher])

  function trocarOTexto(novo: string) {
    setTexto(novo)
    aoTrocarOTexto?.(novo)
  }

  function escolher(grupo: Grupo) {
    setSumiu(false)
    aoEscolher(grupo.id)
    trocarOTexto('')
    setAberta(false)
    setAtiva(-1)
  }

  function aoTeclar(evento: KeyboardEvent<HTMLInputElement>) {
    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      evento.preventDefault()
      setAberta(true)
      if (opcoes.length === 0) {
        return
      }
      const passo = evento.key === 'ArrowDown' ? 1 : -1
      setAtiva((atual) => (atual + passo + opcoes.length) % opcoes.length)
      return
    }
    if (!listaAberta) {
      return
    }
    if (evento.key === 'Enter') {
      // Com a lista aberta o Enter é da lista, e nunca do formulário da ficha.
      evento.preventDefault()
      const opcao = opcoes[ativa]
      if (opcao !== undefined) {
        escolher(opcao)
      } else {
        setAberta(false)
      }
      return
    }
    if (evento.key === 'Escape') {
      evento.preventDefault()
      setAberta(false)
      setAtiva(-1)
    }
  }

  const ativaNaLista = listaAberta && ativa >= 0 && ativa < opcoes.length

  return (
    <div className="campo-de-empresa">
      <label className="campo-de-empresa__rotulo" htmlFor={id}>
        {ROTULO_DO_CAMPO_DO_GRUPO}
      </label>
      <input
        ref={entrada}
        id={id}
        className="campo-de-empresa__entrada"
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={listaAberta}
        aria-controls={listaAberta ? idDaLista : undefined}
        aria-describedby={recusa === null ? idDaDica : `${idDaDica} ${idDaRecusa}`}
        aria-invalid={recusa === null ? undefined : true}
        aria-activedescendant={ativaNaLista ? `${id}-opcao-${String(ativa)}` : undefined}
        placeholder={DICA_DO_CAMPO_DO_GRUPO}
        disabled={desabilitado}
        value={escolhido?.nome ?? texto}
        onFocus={(evento) => {
          setConteiner(conteinerDoPainel(evento.currentTarget))
          setAberta(true)
        }}
        onBlur={() => {
          setAberta(false)
          setAtiva(-1)
          //: O nome de um Grupo só, digitado inteiro: vale como escolha.
          const igual = escolhido === undefined ? grupoDeNomeIgual(grupos, texto) : undefined
          if (igual !== undefined) {
            escolher(igual)
          }
        }}
        onKeyDown={aoTeclar}
        onChange={(evento) => {
          // Qualquer tecla depois de escolher tira a escolha e reabre a lista com o que ficou.
          if (grupoId !== null) {
            aoEscolher(null)
          }
          trocarOTexto(evento.target.value)
          setAtiva(-1)
          setAberta(true)
        }}
      />
      <span id={idDaDica} className="campo-de-empresa__so-para-leitor">
        {DICA_DO_CAMPO_DO_GRUPO}
      </span>
      {escolhido === undefined ? null : (
        <p className="campo-de-empresa__nota">
          {filiaisDoGrupo(escolhido)}{' '}
          <button
            type="button"
            className="etiquetas__botao etiquetas__botao--discreto"
            disabled={desabilitado}
            onClick={() => {
              aoEscolher(null)
            }}
          >
            {TIRAR_O_GRUPO}
          </button>
        </p>
      )}
      {listaAberta
        ? createPortal(
            <div
              ref={popup}
              className="campo-de-empresa__popup campo-de-empresa__popup--flutuante"
              // Antes de medir, a lista existe sem aparecer: a primeira posição é a calculada.
              style={posicao ?? { top: 0, left: 0, visibility: 'hidden' }}
            >
              <ul
                id={idDaLista}
                hidden={opcoes.length === 0}
                className="campo-de-empresa__lista"
                role="listbox"
                aria-label={ROTULO_DO_CAMPO_DO_GRUPO}
              >
                {opcoes.map((grupo, indice) => (
                  <li
                    key={grupo.id}
                    id={`${id}-opcao-${String(indice)}`}
                    role="option"
                    aria-selected={indice === ativa}
                    data-ativa={indice === ativa ? 'sim' : undefined}
                    className="campo-de-empresa__opcao"
                    onMouseDown={(evento) => {
                      evento.preventDefault()
                    }}
                    onClick={() => {
                      escolher(grupo)
                    }}
                  >
                    <span className="campo-de-empresa__nome">{grupo.nome}</span>
                    <span className="campo-de-empresa__detalhe">{filiaisDoGrupo(grupo)}</span>
                  </li>
                ))}
              </ul>
              {opcoes.length === 0 ? (
                <p className="campo-de-empresa__nota">
                  {grupos.length === 0 ? SEM_GRUPOS : NENHUM_GRUPO_ENCONTRADO}
                </p>
              ) : null}
            </div>,
            conteiner ?? document.body,
          )
        : null}
      {sumiu && escolhido === undefined ? (
        <p className="campo-de-empresa__aviso" role="status">
          {GRUPO_INDISPONIVEL}
        </p>
      ) : null}
      {recusa === null ? null : (
        <p className="campo-de-empresa__erro" id={idDaRecusa} role="alert">
          {recusa}
        </p>
      )}
      {consulta.isError ? (
        <p className="campo-de-empresa__erro" role="alert">
          {consulta.error instanceof ErroDaApi
            ? consulta.error.message
            : MENSAGEM_DE_ERRO_DESCONHECIDO}
        </p>
      ) : null}
    </div>
  )
}
